import React, { useState, useEffect, useRef } from 'react';
import './TextArea.css';

const TEXT_LIMIT = 50000;
const DEBOUNCE_DELAY = 1000; // 1 second

function TextArea({ onSend, initialText = '' }) {
  const [text, setText] = useState(initialText);
  const [copySuccess, setCopySuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const debounceTimer = useRef(null);
  const lastSentText = useRef(initialText);
  const isRemoteUpdate = useRef(false);

  // Update text when initialText changes (from other users)
  useEffect(() => {
    if (initialText !== text && initialText !== lastSentText.current) {
      isRemoteUpdate.current = true;
      setText(initialText);
      lastSentText.current = initialText;
    }
  }, [initialText, text]);

  useEffect(() => {
    setCharCount(text.length);

    // Skip sending if this was a remote update
    if (isRemoteUpdate.current) {
      isRemoteUpdate.current = false;
      return;
    }

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Send if text has changed
    if (text !== lastSentText.current) {
      debounceTimer.current = setTimeout(() => {
        onSend(text);
        lastSentText.current = text;
      }, DEBOUNCE_DELAY);
    }

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, onSend]);

  const handleChange = (e) => {
    const newText = e.target.value;
    if (newText.length <= TEXT_LIMIT) {
      setText(newText);
    }
  };

  const handleClear = () => {
    setText('');
    onSend('');
    lastSentText.current = '';
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const isNearLimit = charCount > TEXT_LIMIT * 0.9;
  const isAtLimit = charCount >= TEXT_LIMIT;

  return (
    <div className="text-area-container">
      <textarea
        className="text-area"
        placeholder="Text hier eingeben... (wird automatisch synchronisiert)"
        value={text}
        onChange={handleChange}
        rows={8}
      />
      
      <div className="text-area-footer">
        <div className={`char-counter ${isNearLimit ? 'warning' : ''} ${isAtLimit ? 'danger' : ''}`}>
          {charCount.toLocaleString()} / {TEXT_LIMIT.toLocaleString()} Zeichen
        </div>
        
        {text && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-primary btn-small"
              onClick={handleCopy}
              style={copySuccess ? { backgroundColor: '#10b981' } : {}}
            >
              {copySuccess ? 'Kopiert!' : 'Kopieren'}
            </button>
            <button
              className="btn btn-secondary btn-small"
              onClick={handleClear}
            >
              Löschen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TextArea;
